<template>
  <div class="camera-management">
    <!-- 顶部导航栏 -->
    <van-nav-bar
      title="摄像头管理"
      left-text="返回"
      left-arrow
      @click-left="goBack"
      class="nav-bar"
    />
    
    <!-- 页面标题区域 -->
    <div class="page-header">
      <div class="page-title">
        <van-icon name="setting-o" class="title-icon" />
        <span>设备摄像头管理</span>
      </div>
      <div class="page-subtitle">管理设备关联的摄像头信息</div>
    </div>
    
    <!-- 设备信息 -->
    <div class="device-info">
      <van-card class="device-card">
        <template #title>
          <div class="device-header">
            <h3>{{ deviceInfo.deviceName || '设备信息' }}</h3>
            <!-- <van-tag :type="deviceInfo.isOnlineAi ? 'success' : 'default'" size="small">
              {{ deviceInfo.isOnlineAi ? 'AI在线' : 'AI离线' }}
            </van-tag> -->
          </div>
        </template>
        <template #desc>
          <div class="device-details">
            <div class="detail-row">
              <span class="label">设备编号：</span>
              <span class="value">{{ deviceInfo.deviceSN }}</span>
            </div>
            <div class="detail-row">
              <span class="label">所属码头：</span>
              <span class="value">{{ getPortDesc(deviceInfo.portCode) }}</span>
            </div>
          </div>
        </template>
      </van-card>
    </div>

    <!-- 操作栏 -->
    <div class="action-bar">
      <van-button 
        type="primary" 
        size="small" 
        icon="plus" 
        @click="showAddCamera = true"
      >
        添加摄像头
      </van-button>
    </div>

    <!-- 摄像头列表 -->
    <div class="camera-list">
      <van-card
        v-for="camera in cameras"
        :key="camera.id"
        class="camera-card"
        @click="onCameraClick(camera)"
      >
        <template #title>
          <div class="camera-header">
            <div class="camera-info">
              <h4 class="camera-id">{{ camera.deviceSN }}</h4>
              <van-tag :type="getCameraTypeTagType(camera.type)" size="small">
                {{ getCameraTypeText(camera.type) }}
              </van-tag>
              <!-- <van-tag :type="camera.isOnline ? 'success' : 'default'" size="small" style="margin-left: 8px;">
                {{ camera.isOnline ? '在线' : '离线' }}
              </van-tag> -->
            </div>
          </div>
        </template>
        <template #desc>
          <div class="camera-details">
            <div class="detail-row">
              <span class="label">IP地址：</span>
              <span class="value">{{ formatDateTime(camera.ip) }}</span>
            </div>
            <div class="detail-row">
              <span class="label">心跳时间：</span>
              <span class="value">{{ formatDateTime(camera.heatBeatTime) }}</span>
            </div>
            <div class="detail-row">
              <span class="label">创建时间：</span>
              <span class="value">{{ formatDateTime(camera.createTime) }}</span>
            </div>
            <div class="detail-row">
              <span class="label">所属码头：</span>
              <span class="value">{{ getPortDesc(camera.portCode) }}</span>
            </div>
          </div>
        </template>
      </van-card>
    </div>

    <!-- 空状态 -->
    <van-empty
      v-if="cameras.length === 0"
      description="暂无摄像头数据"
    >
      <template #image>
        <svg width="120" height="80" viewBox="0 0 120 80" fill="none" xmlns="http://www.w3.org/2000/svg">
          <rect x="20" y="30" width="80" height="40" rx="4" fill="none" stroke="#d9d9d9" stroke-width="2"/>
          <circle cx="60" cy="50" r="8" fill="none" stroke="#d9d9d9" stroke-width="2"/>
          <rect x="75" y="35" width="15" height="10" rx="2" fill="none" stroke="#d9d9d9" stroke-width="2"/>
        </svg>
      </template>
    </van-empty>

    <!-- 添加摄像头弹窗 -->
    <van-popup v-model="showAddCamera" position="bottom" :style="{ height: '50%' }" round>
      <div class="popup-header">
        <h3>添加摄像头</h3>
        <van-icon name="cross" @click="showAddCamera = false" />
      </div>
      <van-form @submit="onAddCamera" class="camera-form">
        <van-field
          v-model="addCameraForm.deviceSN"
          name="deviceSN"
          label="摄像头编号"
          placeholder="请输入摄像头编号"
          :rules="[{ required: true, message: '请输入摄像头编号' }]"
        />
        <van-field
          v-model="addCameraForm.ip"
          name="ip"
          label="IP地址"
          placeholder="请输入IP地址"
          :rules="[
            { required: true, message: '请输入IP地址' },
            { validator: validateIPAddress, message: '请输入正确的IP地址格式' }
          ]"
        />
        <van-field
          name="type"
          label="摄像头类型"
          placeholder="请选择摄像头类型"
          readonly
          is-link
          v-model="addCameraForm.typeDesc"
          @click="showAddTypePicker = true"
          :rules="[{ required: true, message: '请选择摄像头类型' }]"
        />
        <div class="form-actions">
          <van-button
            round
            block
            type="primary"
            native-type="submit"
            :loading="addCameraLoading"
          >
            确认添加
          </van-button>
        </div>
      </van-form>
    </van-popup>

    <!-- 添加摄像头类型选择器 -->
    <van-popup v-model="showAddTypePicker" position="bottom">
      <van-picker
        :columns="typeColumns"
        show-toolbar
        title="选择摄像头类型"
        @confirm="onAddTypeConfirm"
        @cancel="showAddTypePicker = false"
      />
    </van-popup>

    <!-- 编辑摄像头弹窗 -->
    <van-popup v-model="showEditCamera" position="bottom" :style="{ height: '50%' }" round>
      <div class="popup-header">
        <h3>编辑摄像头</h3>
        <van-icon name="cross" @click="showEditCamera = false" />
      </div>
      <van-form class="camera-form">
        <van-field
          v-model="editCameraForm.deviceSN"
          name="deviceSN"
          label="摄像头编号"
          placeholder="请输入摄像头编号"
          :rules="[{ required: true, message: '请输入摄像头编号' }]"
        />
        <van-field
          v-model="editCameraForm.ip"
          name="ip"
          label="IP地址"
          placeholder="请输入IP地址"
          :rules="[
            { required: true, message: '请输入IP地址' },
            { validator: validateIPAddress, message: '请输入正确的IP地址格式' }
          ]"
        />
        <van-field
          name="type"
          label="摄像头类型"
          placeholder="请选择摄像头类型"
          readonly
          v-model="editCameraForm.typeDesc"
          :rules="[{ required: true, message: '请选择摄像头类型' }]"
        />
        <div class="form-actions">
          <van-button
            round
            block
            type="primary"
            :loading="editCameraLoading"
            @click="onEditCamera"
          >
            保存修改
          </van-button>
        </div>
        <div class="danger-actions">
          <van-button
            round
            block
            type="danger"
            :loading="deleteCameraLoading"
            @click="onDeleteCamera"
          >
            删除摄像头
          </van-button>
        </div>
      </van-form>
    </van-popup>



    <!-- 删除摄像头确认弹窗 -->
    <van-popup
      v-model="showDeleteCameraConfirm"
      :style="{ width: '85%', borderRadius: '16px' }"
      round
      :close-on-click-overlay="false"
    >
      <div class="custom-confirm-dialog">
        <div class="confirm-icon">
          <van-icon
            name="delete-o"
            color="#ee0a24"
            size="48"
          />
        </div>
        <div class="confirm-title">
          删除摄像头
        </div>
        <div class="confirm-message">
          确定要删除该摄像头吗？此操作不可恢复！
        </div>
        <div class="confirm-actions">
          <van-button
            round
            size="small"
            plain
            @click="cancelDeleteCameraConfirm"
            :disabled="deleteCameraConfirmConfig.loading"
          >
            取消
          </van-button>
          <van-button
            round
            size="small"
            type="danger"
            :loading="deleteCameraConfirmConfig.loading"
            @click="executeDeleteCameraConfirm"
          >
            删除摄像头
          </van-button>
        </div>
      </div>
    </van-popup>
  </div>
</template>

<script>
import globalDic, { fetchMultiDic } from '@/utils/dic';

export default {
  name: 'CameraManagement',
  data() {
    return {
      deviceId: '',
      deviceOnline: false,
      mac: '',
      deviceInfo: {},
      cameras: [],
      // 添加摄像头相关
      showAddCamera: false,
      addCameraForm: {
        deviceSN: '',
        ip: '',
        type: 0,
        typeDesc: ''
      },
      showAddTypePicker: false,
      addCameraLoading: false,
      // 编辑摄像头相关
      showEditCamera: false,
      editCameraForm: {
        id: '',
        deviceSN: '',
        ip: '',
        type: 0,
        typeDesc: ''
      },

      editCameraLoading: false,
      deleteCameraLoading: false,
      showDeleteCameraConfirm: false,
      deleteCameraConfirmConfig: {
        loading: false
      }
    };
  },
  computed: {
    typeColumns() {
      return ['人脸摄像头', 'AI摄像头'];
    }
  },
  async mounted() {
    if (!globalDic.D_Port || globalDic.D_Port.length === 0) {
      await fetchMultiDic(['D_Role', 'D_Port', 'D_AlarmType']);
    }
    this.deviceId = this.$route.params.deviceId;
    this.mac = this.$route.params.mac;
    this.deviceOnline = this.$route.params.isOnlineAi;
    this.loadDeviceInfo();
    this.loadCameras();
  },
  methods: {
    goBack() {
      this.$router.back();
    },
    loadDeviceInfo() {
      // 这里可以根据deviceId获取设备信息，暂时使用路由参数
      this.deviceInfo = {
        deviceSN: this.deviceId,
        deviceName: `设备${this.deviceId}`,
        portCode: '000001',
        isOnlineAi: this.deviceOnline
      };
    },
    loadCameras() {
      this.$axios = this.$axios || require('@/utils/request').default;
      this.$axios.post('/api/TKCamera/Query', {
        bindingTKId: this.deviceId
      })
        .then(res => {
          if (res && res.data && res.data.success) {
            this.cameras = res.data.data || [];
          } else {
            this.$toast.fail('获取摄像头列表失败');
          }
        })
        .catch(() => {
          this.$toast.fail('获取摄像头列表失败');
        });
    },
    getCameraTypeText(type) {
      switch (Number(type)) {
        case 0: return '人脸摄像头';
        case 1: return 'AI摄像头';
        default: return '未知类型';
      }
    },
    getCameraTypeTagType(type) {
      switch (Number(type)) {
        case 0: return 'primary';
        case 1: return 'success';
        default: return 'default';
      }
    },
    getPortDesc(portCode) {
      const dic = globalDic.D_Port || [];
      const found = dic.find(item => item.code === portCode);
      return found ? found.description : portCode;
    },
    formatDateTime(str) {
      if (!str) return '';
      const d = new Date(str);
      if (isNaN(d.getTime())) return str;
      return d.getFullYear() + '-' + (d.getMonth()+1).toString().padStart(2,'0') + '-' + d.getDate().toString().padStart(2,'0') + ' ' + d.getHours().toString().padStart(2,'0') + ':' + d.getMinutes().toString().padStart(2,'0') + ':' + d.getSeconds().toString().padStart(2,'0');
    },
    
    // IP地址校验
    validateIPAddress(value) {
      if (!value) return false;
      
      // IPv4地址正则表达式
      const ipv4Regex = /^(?:(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.){3}(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)$/;
      
      // 检查是否为有效的IPv4地址
      if (!ipv4Regex.test(value)) {
        return false;
      }
      
      // 检查是否为私有IP地址或特殊IP地址
      const parts = value.split('.');
      const firstOctet = parseInt(parts[0]);
      const secondOctet = parseInt(parts[1]);
      
      // 检查是否为特殊IP地址
      if (firstOctet === 0 || firstOctet === 127 || firstOctet === 255) {
        return false;
      }
      
      // 检查是否为有效的私有IP地址范围
      if (firstOctet === 10) {
        return true; // 10.0.0.0/8
      }
      
      if (firstOctet === 172 && secondOctet >= 16 && secondOctet <= 31) {
        return true; // 172.16.0.0/12
      }
      
      if (firstOctet === 192 && secondOctet === 168) {
        return true; // 192.168.0.0/16
      }
      
      // 检查是否为有效的公网IP地址
      if (firstOctet >= 1 && firstOctet <= 223) {
        return true;
      }
      
      return false;
    },
    // 添加摄像头相关
    onAddTypeConfirm(value) {
      this.addCameraForm.typeDesc = value;
      this.addCameraForm.type = value === '人脸摄像头' ? 0 : 1;
      this.showAddTypePicker = false;
    },
    onAddCamera() {
      this.addCameraLoading = true;
      this.$axios = this.$axios || require('@/utils/request').default;
      this.$axios.post('/api/TKCamera/Add', {
        deviceSN: this.addCameraForm.deviceSN,
        ip: this.addCameraForm.ip,
        type: this.addCameraForm.type,
        bindingTKId: this.deviceId
      }).then(res => {
        if (res && res.data && res.data.success) {
          this.$toast.success('摄像头添加成功');
          this.showAddCamera = false;
          this.addCameraForm = { deviceSN: '', ip: '', type: 0, typeDesc: '' };
          this.loadCameras();
        } else {
          this.$toast.fail(res.data.message || '摄像头添加失败');
        }
      }).catch(() => {
        this.$toast.fail('摄像头添加失败');
      }).finally(() => {
        this.addCameraLoading = false;
      });
    },
    // 编辑摄像头相关
    onCameraClick(camera) {
      this.editCameraForm = {
        id: camera.id,
        deviceSN: camera.deviceSN,
        ip: camera.ip || '',
        type: camera.type,
        typeDesc: this.getCameraTypeText(camera.type)
      };
      this.showEditCamera = true;
    },

    onEditCamera() {
      this.editCameraLoading = true;
      this.$axios = this.$axios || require('@/utils/request').default;
      this.$axios.post('/api/TKCamera/Modify', {
        id: this.editCameraForm.id,
        deviceSN: this.editCameraForm.deviceSN,
        ip: this.editCameraForm.ip
      }).then(res => {
        if (res && res.data && res.data.success) {
          this.$toast.success('摄像头修改成功');
          this.showEditCamera = false;
          this.loadCameras();
        } else {
          this.$toast.fail(res.data.message || '摄像头修改失败');
        }
      }).catch(() => {
        this.$toast.fail('摄像头修改失败');
      }).finally(() => {
        this.editCameraLoading = false;
      });
    },
    onDeleteCamera() {
      this.deleteCameraConfirmConfig.loading = false;
      this.showDeleteCameraConfirm = true;
    },
    cancelDeleteCameraConfirm() {
      this.showDeleteCameraConfirm = false;
    },
    executeDeleteCameraConfirm() {
      if (this.deleteCameraConfirmConfig.loading) return;
      this.deleteCameraConfirmConfig.loading = true;
      this.$axios = this.$axios || require('@/utils/request').default;
      this.$axios.post('/api/TKCamera/Delete', {
        id: this.editCameraForm.id
      }).then(res => {
        if (res && res.data && res.data.success) {
          this.$toast.success('摄像头删除成功');
          this.showDeleteCameraConfirm = false;
          this.showEditCamera = false;
          this.loadCameras();
        } else {
          this.$toast.fail(res.data.message || '摄像头删除失败');
        }
      }).catch(() => {
        this.$toast.fail('摄像头删除失败');
      }).finally(() => {
        this.deleteCameraConfirmConfig.loading = false;
      });
    }
  }
};
</script>

<style scoped>
.camera-management {
  background-color: #f5f7fa;
  min-height: 100vh;
  overflow-y: auto;
}

.nav-bar {
  background: #e9ecef !important;
  color: #495057 !important;
  border-bottom: 3px solid #dee2e6 !important;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.1) !important;
  height: 44px !important;
}

.nav-bar :deep(.van-nav-bar__title) {
  color: #343a40 !important;
  font-weight: 600 !important;
  font-size: 14px !important;
  letter-spacing: 0.3px !important;
}

.nav-bar :deep(.van-nav-bar__left) {
  color: #6c757d !important;
}

.nav-bar :deep(.van-nav-bar__text) {
  color: #6c757d !important;
  font-size: 14px !important;
}

.nav-bar :deep(.van-icon) {
  color: #6c757d !important;
  font-size: 16px !important;
}

.page-header {
  padding: 16px 12px 8px 12px;
  background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
  border-bottom: 1px solid #dee2e6;
}

.page-title {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 18px;
  font-weight: 600;
  color: #495057;
  margin-bottom: 4px;
}

.title-icon {
  color: #6c757d;
  font-size: 20px;
}

.page-subtitle {
  font-size: 13px;
  color: #6c757d;
  margin-left: 28px;
}

.popup-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px;
  border-bottom: 1px solid #ebedf0;
}
.popup-header h3 {
  margin: 0;
  font-size: 18px;
  font-weight: bold;
  color: #323233;
}
.popup-header .van-icon {
  font-size: 22px;
  color: #969799;
  cursor: pointer;
}

.device-info {
  margin: 12px;
  margin-bottom: 16px;
}
.device-card {
  border-radius: 8px;
  overflow: hidden;
  padding: 10px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.08);
  border: 1px solid #e9ecef;
}
.device-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 8px;
}
.device-header h3 {
  margin: 0;
  font-size: 16px;
  font-weight: bold;
}

.action-bar {
  margin: 0 12px 16px 12px;
  display: flex;
  justify-content: flex-end;
}

.camera-list {
  margin: 0 12px;
  display: flex;
  flex-direction: column;
  gap: 6px;
}
.camera-card {
  border-radius: 8px;
  overflow: hidden;
  padding: 10px;
}
.camera-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 8px;
  width: 100%;
}
.camera-info {
  display: flex;
  align-items: center;
  gap: 8px;
}
.camera-id {
  margin: 0;
  font-size: 16px;
  font-weight: bold;
}

.detail-row {
  display: flex;
  margin-bottom: 5px;
  font-size: 14px;
}
.label {
  color: #646566;
  min-width: 80px;
}
.value {
  color: #323233;
  flex: 1;
}

.camera-form {
  padding: 16px;
}
.form-actions {
  margin-top: 24px;
}
.danger-actions {
  margin-top: 16px;
  padding-top: 16px;
  border-top: 1px solid #ebedf0;
}

.custom-confirm-dialog {
  padding: 32px 24px 24px;
  text-align: center;
  background: white;
  border-radius: 16px;
}
.confirm-icon {
  margin-bottom: 16px;
  display: flex;
  justify-content: center;
}
.confirm-title {
  font-size: 18px;
  font-weight: bold;
  color: #323233;
  margin-bottom: 12px;
}
.confirm-message {
  font-size: 14px;
  color: #646566;
  line-height: 1.5;
  margin-bottom: 24px;
}
.confirm-actions {
  display: flex;
  gap: 12px;
  justify-content: center;
}
.confirm-actions .van-button {
  min-width: 80px;
  height: 36px;
}

@media (prefers-color-scheme: dark) {
  .camera-management {
    background-color: #1a1a1a;
  }
  
  .page-header {
    background: linear-gradient(135deg, #2a2a2a 0%, #333 100%);
    border-bottom-color: #444;
  }
  
  .page-title {
    color: #ccc;
  }
  
  .title-icon {
    color: #999;
  }
  
  .page-subtitle {
    color: #999;
  }
  
  .device-card {
    background-color: #2a2a2a;
    border-color: #444;
  }
  
  .camera-card {
    background-color: #2a2a2a;
    border-color: #444;
  }
  
  .camera-id {
    color: #fff;
  }
  
  .label {
    color: #aaa;
  }
  
  .value {
    color: #ddd;
  }
  
  .nav-bar {
    background: #333 !important;
    color: #ccc !important;
    border-bottom-color: #555 !important;
  }
  
  .nav-bar :deep(.van-nav-bar__title) {
    color: #e9ecef !important;
  }
  
  .nav-bar :deep(.van-nav-bar__left) {
    color: #999 !important;
  }
  
  .nav-bar :deep(.van-nav-bar__text) {
    color: #999 !important;
  }
  
  .nav-bar :deep(.van-icon) {
    color: #999 !important;
  }
}
</style> 